import { array, concat, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import findBy from '@nullvoxpopuli/ember-composable-helpers/helpers/find-by';
import { format, subDays, parseISO, startOfDay } from 'date-fns';
import FocusTrapper from 'ember-focus-trapper/components/focus-trapper';
import PowerSelect from 'ember-power-select/components/power-select';
import { eq, not, or } from 'ember-truth-helpers';
import AnalyticsExportModal from 'garaje/components/analytics/export-modal';
import DateRanger from 'garaje/components/date-ranger';
import DocumentColumn from 'garaje/components/document-column';
import EmbeddedApp from 'garaje/components/embedded-app';
import VisitorSearch from 'garaje/components/location-overview/visitor-log/visitor-search';
import MonogramOrPhoto from 'garaje/components/monogram-or-photo';
import MultiModelSelector from 'garaje/components/multi-model-selector';
import SortableColumnHeader from 'garaje/components/sortable-column-header';
import UiClassicLoadingSpinnerPulse from 'garaje/components/ui/classic/loading-spinner-pulse';
import ModalDialog from 'garaje/components/ui/classic/modal-dialog';
import UiHeader from 'garaje/components/ui/header';
import UiPagination from 'garaje/components/ui-pagination';
import UiPaginationArrow from 'garaje/components/ui-pagination/arrow';
import UiPaginationLabel from 'garaje/components/ui-pagination/label';
import dateFormat from 'garaje/helpers/date-format';
import featureEnabled from 'garaje/helpers/feature-enabled';
import type EntryModel from 'garaje/models/entry';
import type GroupModel from 'garaje/models/group';
import type SkinnyLocationModel from 'garaje/models/skinny-location';
import type { LocationOverviewVisitorLogRouteModel } from 'garaje/pods/location-overview/visitor-log/route';
import type { EntryExportParams } from 'garaje/services/async-export-manager';
import type AsyncExportManagerService from 'garaje/services/async-export-manager';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type MessageBusService from 'garaje/services/message-bus';
import type StateService from 'garaje/services/state';
import type { RecordArray } from 'garaje/utils/type-utils';
import urlBuilder from 'garaje/utils/url-builder';
import { or as orMacro, uniqueBy, reads } from 'macro-decorators';

const SIGNED_OUT_FILTERS = [
  { name: 'All visitors' },
  { name: 'Currently signed in', val: 'not-signed-out' },
  { name: 'Signed out', val: 'signed-out' },
];

const EXPORT_FORMATS = [
  {
    name: 'CSV',
    val: 'csv',
    desc: 'Download a CSV file. Data exports as a single sheet; location name will appear in a column.',
  },
  {
    name: 'XLSX',
    val: 'xlsx',
    desc: 'Download an Excel file. Each location’s data will appear on a separate sheet.',
  },
];

const DATE_RANGE_SHORTCUTS = [
  'today',
  'yesterday',
  'past7Days',
  'past30Days',
  'past60Days',
  'past90Days',
  'monthToDate',
  'yearToDate',
  'pastYear',
  'allTime',
];

interface VisitorLogTableComponentSignature {
  Args: {
    entries: RecordArray<EntryModel>;
    params: LocationOverviewVisitorLogRouteModel['params'];
    pageNumber: number;
    pageSize: number;
    setPageNumber: (pageNumber: number) => void;
    sortDirection: string | undefined;
    setSortDirection: (sortDirection: string) => void;
    sortBy?: string;
    setSortBy: (sortBy: string) => void;
    query?: string;
    setQuery: (query?: string) => void;
    availableLocations: SkinnyLocationModel[];
    groups?: GroupModel[] | RecordArray<GroupModel>;
    locationIds?: string;
    setLocationIds: (locationIds?: string) => void;
    signedOut?: string;
    setSignedOut: (signedOut?: string) => void;
    startDate: string;
    endDate: string;
    setDateRange: (startDate: Date, endDate: Date) => void;
    loadingData: boolean;
  };
}

export default class VisitorLogTable extends Component<VisitorLogTableComponentSignature> {
  @service declare state: StateService;
  @service declare messageBus: MessageBusService;
  @service declare asyncExportManager: AsyncExportManagerService;
  @service declare featureFlags: FeatureFlagsService;

  @tracked showExportModal = false;
  @tracked selectedExportFormat;

  defaultRecordCount = 0;

  companyId: string | null = null;
  defaultLocationFilter;
  signedOutFilters;
  exportFormats;

  shortcuts = DATE_RANGE_SHORTCUTS;

  @orMacro('args.sortBy', 'args.sortDirection', 'args.locationIds', 'args.signedOut') isFiltered!: boolean;
  @reads('args.query') isSearched!: string;
  @uniqueBy('args.availableLocations', 'id') uniqLocations!: SkinnyLocationModel[];

  @orMacro('args.entries.meta.total', 'defaultRecordCount') recordCount!: number;

  get allTimeMin(): Date | undefined {
    return this.state.currentCompany.createdAt ? startOfDay(this.state.currentCompany.createdAt) : undefined;
  }

  get pageCount(): number {
    return Math.ceil(this.recordCount / this.args.pageSize);
  }

  get noResultMessage(): string {
    if (this.isFiltered && !this.isSearched) {
      return 'Try changing your filters.';
    } else if (this.isFiltered && this.isSearched) {
      return 'Try adjusting your search or changing your filters.';
    } else if (!this.isFiltered && this.isSearched) {
      return 'Try adjusting your search.';
    }

    return '';
  }

  get locationOptions(): Partial<SkinnyLocationModel>[] {
    return [this.defaultLocationFilter, ...this.uniqLocations];
  }

  get locationIdForExport(): string {
    let locationIds = this.args.locationIds ?? '';
    if (locationIds === '') {
      locationIds = this.args.availableLocations.map((loc) => loc.id).join(',');
    }
    return locationIds;
  }

  get startDateForExport(): string {
    if (this.args.startDate === '' && this.args.endDate === '') {
      return format(this.state.currentCompany.createdAt, 'yyyy-MM-dd');
    } else if (this.args.startDate) {
      return format(parseISO(this.args.startDate), 'yyyy-MM-dd');
    } else {
      return format(subDays(new Date(), 30), 'yyyy-MM-dd');
    }
  }

  get endDateForExport(): string {
    if (this.args.startDate === '' && this.args.endDate === '') {
      return format(new Date(), 'yyyy-MM-dd');
    } else if (this.args.endDate) {
      return format(parseISO(this.args.endDate), 'yyyy-MM-dd');
    } else {
      return format(new Date(), 'yyyy-MM-dd');
    }
  }

  get labelForExport(): string {
    const locationIds = this.locationIdForExport;
    const locationCount = locationIds.split(',').length;
    if (this.args.availableLocations.length === locationCount) {
      return 'All locations';
    } else if (locationCount > 1) {
      return locationCount + ` locations`;
    } else {
      return this.args.availableLocations.find((loc) => loc.id === locationIds)?.name ?? 'All locations';
    }
  }

  get oldExportIframeUrl(): string {
    return urlBuilder.embeddedEntryExportModalUrl(
      this.companyId!,
      this.locationIdForExport,
      null,
      this.startDateForExport,
      this.endDateForExport,
      this.labelForExport,
    );
  }

  constructor(...args: ConstructorParameters<typeof Component<VisitorLogTableComponentSignature>>) {
    super(...args);

    this.defaultLocationFilter = {
      name: 'All locations',
    };
    this.exportFormats = EXPORT_FORMATS;
    this.selectedExportFormat = EXPORT_FORMATS[0];
    this.signedOutFilters = SIGNED_OUT_FILTERS;
    this.companyId = this.state.currentCompany.id;
    // eslint-disable-next-line @typescript-eslint/unbound-method
    this.messageBus.on('embedded-app-message', this, this.handleMessage);
  }

  @action
  toggleModal(): void {
    const modalStatus = this.showExportModal;
    this.showExportModal = !modalStatus;
  }

  handleMessage(message: { event: string }): void {
    if (message.event === 'showEntryExportModal') {
      this.toggleModal();
    } else if (message.event === 'closeExportModal') {
      this.toggleModal();
    }
  }

  @action
  exportEntries(withFilters: boolean): unknown {
    this.showExportModal = false;

    const startDate = this.args.params.filter?.startDate ?? '';
    const endDate = this.args.params.filter?.endDate ?? '';

    let params: EntryExportParams & { action_origin: string } = {
      sort: '',
      query: '',
      startDate: startDate ? parseISO(startDate) : undefined,
      endDate: endDate ? parseISO(endDate) : undefined,
      filter: '',
      exportFormat: this.selectedExportFormat?.val ?? 'csv',
      action_origin: 'location_overview',
    };

    if (!withFilters) {
      return this.asyncExportManager.exportEntriesTask.perform(params);
    }

    params = Object.assign({}, params, {
      sort: this.args.params.sort ?? '',
      query: this.args.params.filter?.query ?? '',
      filter: this.args.params.filter?.status ?? '',
      exportFormat: this.selectedExportFormat?.val ?? 'csv',
      locationIds: (this.args.params.filter?.location ?? '').split(',').filter((id) => id),
    });

    return this.asyncExportManager.exportEntriesTask.perform(params);
  }

  @action
  clearAllFilters(): void {
    this.args.setQuery();
    this.args.setSignedOut();
    this.args.setLocationIds('');
  }

  @action
  resetPageNumber(): void {
    this.args.setPageNumber(1);
  }

  @action
  selectLocationsFilter(options: SkinnyLocationModel[]): void {
    const locationIds = this.args.locationIds ?? '';
    let ids = locationIds.split(',').filter((el) => isPresent(el));
    options.forEach((option) => {
      ids = ids.includes(option.id) ? ids.filter((id) => id !== option.id) : [...ids, option.id];
    });
    this.args.setLocationIds(ids.join(','));
  }

  @action
  selectSignedOutFilter(signedOutFilter: { val: string }): void {
    this.args.setSignedOut(signedOutFilter.val);
  }

  @action
  setExportFormat(value: this['selectedExportFormat']): void {
    this.selectedExportFormat = value;
  }

  <template>
    <div class="flex items-center">
      <UiHeader class="flex-auto" data-test-visitor-log-heading>
        Visitor log
      </UiHeader>
      <div class="w-72">
        <VisitorSearch
          @searchValue={{@query}}
          @setQuery={{@setQuery}}
          @searchValueHasChanged={{this.resetPageNumber}}
          @updateSearchValue={{@setQuery}}
        />
      </div>
    </div>

    {{! Top options bar }}
    <div class="flex flex-wrap gap-2 lg:gap-4 items-start">
      <div class="flex-auto flex flex-wrap gap-2 items-center">
        <DateRanger
          class="px-4"
          @onDateSelected={{@setDateRange}}
          @onDateRangeSelected={{@setDateRange}}
          @shortcuts={{this.shortcuts}}
          @rangeOptions={{hash allTimeMin=this.allTimeMin}}
          date-test-date-ranger
        />

        <UiPagination
          @pageCount={{this.pageCount}}
          @pageNumber={{@pageNumber}}
          @pageSize={{@pageSize}}
          @recordCount={{this.recordCount}}
          @setPageNumber={{@setPageNumber}}
          as |pagination|
        >
          <UiPaginationArrow
            @direction="left"
            @disabled={{not pagination.hasPreviousPage}}
            @onClick={{pagination.goToPreviousPage}}
          />
          <UiPaginationArrow
            @direction="right"
            @disabled={{pagination.isLastPage}}
            @onClick={{pagination.goToNextPage}}
          />
          <UiPaginationLabel class="ml-4 whitespace-nowrap align-middle">
            {{#if pagination.totalCount}}
              <span class="font-bold">{{pagination.start}}&ndash;{{pagination.end}}</span>
              of
              <span class="bold">{{pagination.totalCount}}</span>
              visitors
            {{else if @loadingData}}
              <div class="skeleton-item bg-carbon-5 rounded-lg h-[14px] w-52"></div>
            {{else}}
              <span>No results found</span>
            {{/if}}
          </UiPaginationLabel>
        </UiPagination>
      </div>

      <div class="flex flex-wrap gap-2 items-center">
        <MultiModelSelector
          data-test-location-filter
          @loading={{@loadingData}}
          {{! @glint-expect-error - this component is doing too much dynamic stuff for glint to understand }}
          @groups={{@groups}}
          @selectedIds={{@locationIds}}
          {{! @glint-expect-error - this component is doing too much dynamic stuff for glint to understand }}
          @items={{this.uniqLocations}}
          {{! @glint-expect-error - this component is doing too much dynamic stuff for glint to understand }}
          @onSelected={{this.selectLocationsFilter}}
          class="w-40 truncate !my-0"
        />

        <div data-test-signed-in-filter>
          <PowerSelect
            @dropdownClass="powerSelect-radio"
            @selected={{findBy "val" @signedOut this.signedOutFilters}}
            @options={{this.signedOutFilters}}
            @searchEnabled={{false}}
            @triggerClass={{concat (if @signedOut "not-default-filter-selected ") "!mb-0 w-40 truncate"}}
            @onChange={{this.selectSignedOutFilter}}
            as |filter|
          >
            {{filter.name}}
          </PowerSelect>
        </div>

        <button class="btn btn--outlineFill" {{on "click" this.toggleModal}} data-test-export>
          Export
        </button>
      </div>
    </div>

    <table data-test-visitor-log-result-table>
      {{! TODO: this will be hidden here for table a11y. Done above for floating header }}
      <thead>
        <tr class="text-sm" data-test-visitor-log-cols>
          <th class="hidden lg:table-cell border-b py-2">
            &nbsp;
          </th>
          <th class="border-b py-2">
            <SortableColumnHeader
              @sortBy="Name"
              @sortDirection={{@sortDirection}}
              @isActive={{eq @sortBy "Name"}}
              @setSortDirection={{@setSortDirection}}
              @setSortBy={{@setSortBy}}
              @sortOptions={{array "A-Z" "Z-A"}}
            />
          </th>
          <th class="border-b py-2">
            <SortableColumnHeader
              @sortBy="Location"
              @sortDirection={{@sortDirection}}
              @isActive={{eq @sortBy "Location"}}
              @setSortDirection={{@setSortDirection}}
              @setSortBy={{@setSortBy}}
              @sortOptions={{array "A-Z" "Z-A"}}
            />
          </th>
          <th class="border-b py-2 text--carbon-50">
            Host
          </th>
          <th class="border-b py-2 text--carbon-50">
            Invited
          </th>
          <th class="border-b py-2">
            <SortableColumnHeader
              @sortBy="Signed in"
              @sortDirection={{@sortDirection}}
              @isActive={{eq @sortBy "Signed in"}}
              @setSortDirection={{@setSortDirection}}
              @setSortBy={{@setSortBy}}
              @sortOptions={{array "Most to least recent" "Least to most recent"}}
            />
          </th>
          <th class="border-b py-2 text--carbon-50">
            NDA
          </th>
        </tr>
      </thead>
      <tbody>
        {{#if @loadingData}}
          <tr>
            <td colspan="7">
              <UiClassicLoadingSpinnerPulse data-test-loading-spinner />
            </td>
          </tr>
        {{else if (not @entries.length)}}
          <tr>
            <td colspan="7">
              <div class="flex flex-col flex-auto items-center py-16">
                {{#if (or this.isSearched this.isFiltered)}}
                  <h3 data-test-visitor-log-no-result class="font-normal text--carbon-50">
                    No matches found
                  </h3>
                  <h4 data-test-visitor-log-no-result-message class="my-2 font-normal text--carbon-50">
                    {{this.noResultMessage}}
                  </h4>
                  <div class="mt-8">
                    <button data-test-clear-all-filters {{on "click" this.clearAllFilters}} class="btn btn--brand">
                      Clear all filters
                    </button>
                  </div>
                {{else}}
                  <img
                    src="/assets/images/empty-state/no-visitors.svg"
                    alt="No visitors"
                    class="empty-visitors"
                    data-test-visitor-log-no-visitors
                  />
                  <h3 data-test-empty-delivery-log-message class="mt-8 font-normal text--carbon-50">
                    No visitors yet today
                  </h3>
                {{/if}}
              </div>
            </td>
          </tr>
        {{else}}
          {{#each @entries as |entry|}}
            <tr data-test-visitor-log-row>
              <td data-test-photo class="hidden lg:table-cell align-middle border-b lg:w-12 lg:pr-2">
                <div class="hidden" data-test-visitor-id={{entry.id}}>{{entry.id}}</div>
                <MonogramOrPhoto
                  @name={{entry.fullName}}
                  @devicePhoto={{entry.thumbnails.small}}
                  @passportPhoto={{entry.passportAvatar}}
                  @borderBottom={{false}}
                />
              </td>
              <td data-test-name data-private class="table-cell align-middle border-b" style="padding-left:8px">
                {{entry.fullName}}
              </td>
              <td data-test-location class="table-cell align-middle border-b text-sm text--carbon-50">
                {{entry.location.content.name}}
              </td>
              <td data-test-host class="table-cell align-middle border-b text-sm text--carbon-50">
                {{entry.host}}
              </td>
              <td data-test-invited class="table-cell align-middle border-b text-sm text--carbon-50">
                {{! @glint-expect-error }}
                {{#if entry.invite}}
                  <img alt="" role="none" src="/assets/images/checkmark-small-gray.svg" />
                  &nbsp;
                  <span class="text--carbon-50">Invited</span>
                {{/if}}
              </td>
              <td data-test-signed-at class="table-cell align-middle border-b text-sm text--carbon-50">
                {{dateFormat entry.signedInAt "MMM d, yyyy - h:mm a"}}
              </td>
              <td data-test-nda class="table-cell align-middle border-b text-sm text--carbon-50">
                <DocumentColumn
                  @content={{entry}}
                  @displayDash={{false}}
                  @isTableCell={{false}}
                  @displayNdaDownload={{true}}
                />
              </td>
            </tr>
          {{/each}}
        {{/if}}
      </tbody>
    </table>

    {{#if (featureEnabled "analytics-improved-exports")}}
      <AnalyticsExportModal
        @isOpen={{this.showExportModal}}
        @onClose={{this.toggleModal}}
        @scope="location"
        @exportType="visitors"
        @locationId={{this.locationIdForExport}}
        @startDate={{this.startDateForExport}}
        @endDate={{this.endDateForExport}}
        @label={{this.labelForExport}}
      />
    {{else}}
      {{#if this.showExportModal}}
        <ModalDialog
          @clickOutsideToClose={{true}}
          @onClose={{this.toggleModal}}
          @translucentOverlay={{true}}
          @containerClass="!max-w-export"
        >
          <FocusTrapper>
            <EmbeddedApp
              @id="visitors-location-analytics-export"
              @url={{this.oldExportIframeUrl}}
              class="-mx-8"
              data-test-export-iframe
            />
          </FocusTrapper>
        </ModalDialog>
      {{/if}}
    {{/if}}
  </template>
}
